import * as yup from "yup";

const hasValue = (value) => typeof value === "string" && value.trim().length > 0;

const findDefinition = (definitions, id) =>
    (definitions ?? []).find((definition) => String(definition.id) === String(id));

const requiresFollowupDetails = (definitions, id) => {
    const definition = findDefinition(definitions, id);
    return definition?.hasfollowup === true || definition?.hasFollowup === true;
};

const requiresStaffFollowup = (definitions, id) => {
    const definition = findDefinition(definitions, id);
    return definition?.hasstafffollowup === true || definition?.hasStaffFollowup === true;
};

const hasDefinitionValue = (value) =>
    value !== undefined && value !== null && value !== "";

const requiredGuidDefinition = (definitions, message) =>
    yup
        .string()
        .required(message)
        .uuid(message)
        .test("valid-definition", message, (value) =>
            !hasDefinitionValue(value) || findDefinition(definitions, value) !== undefined);

const optionalGuidDefinition = (definitions, message) =>
    yup
        .string()
        .transform((value, originalValue) => originalValue === "" ? null : value)
        .nullable()
        .uuid(message)
        .test("valid-definition", message, (value) =>
            !hasDefinitionValue(value) || findDefinition(definitions, value) !== undefined);

const requiredIntegerDefinition = (definitions, message) =>
    yup
        .number()
        .transform((value, originalValue) => originalValue === "" ? undefined : value)
        .typeError(message)
        .integer(message)
        .required(message)
        .test("valid-definition", message, (value) =>
            !hasDefinitionValue(value) || findDefinition(definitions, value) !== undefined);

const optionalIntegerDefinition = (definitions, message) =>
    yup
        .number()
        .transform((value, originalValue) => originalValue === "" ? null : value)
        .nullable()
        .typeError(message)
        .integer(message)
        .test("valid-definition", message, (value) =>
            !hasDefinitionValue(value) || findDefinition(definitions, value) !== undefined);

const optionalString = () =>
    yup
        .string()
        .transform((value, originalValue) => originalValue === "" ? null : value)
        .nullable();

const isValidDateOnly = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
    if (!match) return false;

    const [, year, month, day] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;
};

export const buildIncidentInitialValidationSchema = (definitions = {}) =>
    yup.object().shape({
        waiverType: requiredGuidDefinition(
            definitions.incidentwaivertypes,
            "A valid incident waiver type is required."
        ),
        classification: requiredGuidDefinition(
            definitions.classifications,
            "A valid incident classification is required."
        ),
        incidentDatetime: yup
            .string()
            .required("The incident date and time are required.")
            .test(
                "valid-incident-date-time",
                "Enter a valid incident date and time.",
                (value) => !value || !Number.isNaN(Date.parse(value))
            ),
        reportingPerson: optionalGuidDefinition(
            definitions.reportingperson,
            "Select a valid reporting person."
        ),
        incidentLocation: optionalGuidDefinition(
            definitions.incidentlocations,
            "Select a valid incident location."
        ),
        incidentState: optionalIntegerDefinition(
            definitions.states,
            "Select a valid incident state."
        ),
        codes: yup
            .array()
            .of(
                requiredGuidDefinition(
                    definitions.incidentcodes,
                    "A valid incident code is required."
                )
            )
            .ensure(),
        dateReportCompleted: optionalString().test(
            "valid-report-completed-date",
            "Enter a valid report completed date.",
            (value) => value === null || value === undefined || isValidDateOnly(value)
        ),
        dateTimeReportReceived: optionalString().test(
            "valid-report-received-date-time",
            "Enter a valid report received date and time.",
            (value) => value === null || value === undefined || !Number.isNaN(Date.parse(value))
        ),
        reportingPersonOther: yup
            .string()
            .nullable()
            .test(
                "reporting-person-details-not-allowed",
                "Reporting person details are not allowed when the selected reporting person requires staff follow-up.",
                function (value) {
                    return !requiresStaffFollowup(definitions.reportingperson, this.parent.reportingPerson)
                        || !hasValue(value);
                }
            )
    });

export const buildIncidentNotificationValidationSchema = (notificationTypes = []) =>
    yup.object().shape({
        reportedTo: requiredGuidDefinition(
            notificationTypes,
            "A valid notification recipient is required."
        ),
        reportedToOther: yup
            .string()
            .nullable()
            .test(
                "notification-details",
                "Notification recipient details are required for the selected recipient.",
                function (value) {
                    return !requiresFollowupDetails(notificationTypes, this.parent.reportedTo)
                        || hasValue(value);
                }
            )
    });

export const buildIncidentEmergencyBackupValidationSchema = (backupTypes = []) =>
    yup.object().shape({
        emergencyAssistanceType: requiredGuidDefinition(
            backupTypes,
            "A valid emergency assistance type is required."
        ),
        emergencyAssistanceTypeOther: yup
            .string()
            .nullable()
            .test(
                "emergency-assistance-details",
                "Emergency assistance type details are required for the selected type.",
                function (value) {
                    const requiresDetails = requiresFollowupDetails(
                        backupTypes,
                        this.parent.emergencyAssistanceType
                    );

                    if (requiresDetails) {
                        return hasValue(value);
                    }

                    return !hasValue(value) || this.createError({
                        message: "Emergency assistance type details are only allowed when the selected type requires follow-up."
                    });
                }
            )
    });

export const buildIncidentFollowupStaffValidationSchema = (definitions = {}) =>
    yup.object().shape({
        staffAssignment: requiredIntegerDefinition(
            definitions.usermasterlist,
            "A valid staff assignment is required."
        ),
        followupType: requiredGuidDefinition(
            definitions.followuptype,
            "A valid follow-up type is required."
        )
    });

export const buildIncidentFollowupResponseValidationSchema = (users = []) =>
    yup.object().shape({
        whoCompletedTheAnalysis: requiredIntegerDefinition(
            users,
            "A valid user who completed the analysis is required."
        )
    });
